// src/components/vision/VisionHeroVisual.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { Heart, BrainCircuit, Users, Layers } from 'lucide-react';

export const VisionHeroVisual: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const pathVariants = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: {
      pathLength: 1,
      opacity: 1,
      transition: {
        duration: 2,
        ease: [0.42, 0, 0.58, 1],
      },
    },
  };

  const nodeVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { type: 'spring', stiffness: 150, damping: 20, delay: 1.5 },
    },
  };

  // Data for the orbiting nodes
  const nodes = [
    { Icon: BrainCircuit, angle: 45, color: '#38bdf8' }, // AI (sky-400)
    { Icon: Users, angle: 135, color: '#fb923c' },        // Community (orange-400)
    { Icon: Layers, angle: 225, color: '#c084fc' },       // Platform (purple-400)
  ];

  return (
    <motion.div
      className="w-full max-w-lg mx-auto h-64 md:h-80"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.5 }}
    >
      <svg viewBox="0 0 400 300" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
        <defs>
          <filter id="vision-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Radiating Paths from the Core */}
        {nodes.map((node, i) => (
          <motion.path
            key={`path-${i}`}
            d={`M 200,150 L ${200 + 100 * Math.cos(node.angle * Math.PI / 180)},${150 + 100 * Math.sin(node.angle * Math.PI / 180)}`}
            fill="none"
            stroke="#475569"
            strokeWidth="1.5"
            variants={pathVariants}
          />
        ))}

        {/* Central Core (Heart/Genesis) */}
        <motion.g
          variants={nodeVariants}
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 3, repeat: Infinity, repeatType: 'mirror', ease: 'easeInOut' }}
        >
          <circle cx="200" cy="150" r="22" fill="#1e293b" stroke="#f43f5e" strokeWidth="2" />
          <Heart x="186" y="136" size={28} color="#f43f5e" filter="url(#vision-glow)" />
        </motion.g>

        {/* Orbiting Pillar Nodes */}
        {nodes.map((node, i) => (
          <motion.g
            key={`node-${i}`}
            variants={nodeVariants}
            animate={{
              transform: [
                `translateX(${100 * Math.cos(node.angle * Math.PI / 180)}px) translateY(${100 * Math.sin(node.angle * Math.PI / 180)}px) rotate(0deg)`,
                `translateX(${100 * Math.cos(node.angle * Math.PI / 180)}px) translateY(${100 * Math.sin(node.angle * Math.PI / 180)}px) rotate(360deg)`,
              ]
            }}
            transition={{
              duration: 30 + i * 5,
              repeat: Infinity,
              ease: "linear",
            }}
            style={{ transformOrigin: '200px 150px' }} // Center of the SVG
          >
             <circle cx="200" cy="150" r="18" fill="#1e293b" stroke={node.color} strokeWidth="2" />
             <node.Icon x="188" y="138" size={24} color={node.color} filter="url(#vision-glow)" />
          </motion.g>
        ))}
      </svg>
    </motion.div>
  );
};

export default VisionHeroVisual;